import { useMemo } from 'react'

// Services
import {
  useCancelSurgeryMutation,
  useGetOrgConfigQuery,
  useGetProceduresQuery,
} from 'lib/apollo/hooks'
import { enqueueSnackbar } from 'notistack'
import { useApolloClients } from 'lib/apollo/apolloProvider'

// Types
import {
  CancelProcedureFormData,
  CancelProcedureModalProps,
} from './CancelProcedureModal.types'

// Form hook
import { useForm } from 'react-hook-form'

// State
import { useDateRange, useNotificationActions } from 'store/notifications'
import { loadNotifications } from 'lib/utils/loadNotifications'

export const useCancelProcedureModalLogic = (
  props: CancelProcedureModalProps
) => {
  const { surgery, onClose, refetch } = props

  const dateRange = useDateRange()
  const { setNotifications } = useNotificationActions()

  const [cancelSurgery, { loading: isLoadingCancel }] =
    useCancelSurgeryMutation()
  const { iservicesClient } = useApolloClients()
  const { data: orgConfig } = useGetOrgConfigQuery(iservicesClient)
  const { refetch: refetchProcedures } = useGetProceduresQuery({})

  const cancellationOptions = useMemo(() => {
    const options = []

    if (orgConfig?.getOrgConfig?.data?.cancellationOptions) {
      try {
        const parsedOptions = JSON.parse(
          orgConfig.getOrgConfig.data.cancellationOptions
        )
        options.push(
          ...parsedOptions.map((option: any) => ({
            id: option.value,
            name: option.name,
          }))
        )
      } catch (error) {
        console.error('Error parsing cancellation options:', error)
      }
    }

    return options
  }, [orgConfig?.getOrgConfig?.data?.cancellationOptions])

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CancelProcedureFormData>({
    defaultValues: {
      reason: null,
      customReason: '',
    },
  })

  const selectedReason = watch('reason')
  const isOtherSelected = selectedReason?.id === 'other'

  const onSubmit = async (data: CancelProcedureFormData) => {
    if (!surgery._id || !data.reason?.id) {
      return
    }

    const reasonToSubmit =
      data.reason.id === 'other' ? data.customReason || '' : data.reason.id

    try {
      const response = await cancelSurgery({
        variables: {
          input: {
            surgeryId: surgery._id,
            reason: reasonToSubmit,
          },
        },
      })

      refetchProcedures({
        endDate: dateRange.before,
        startDate: dateRange.after,
        pendingOnly: true,
      })
        .then(({ data }) => {
          const notifications = loadNotifications(data)
          setNotifications(notifications)
        })
        .catch(() => {
          enqueueSnackbar('Failed to cancel procedure', { variant: 'error' })
        })

      if (response.data?.cancelSurgery?.success) {
        enqueueSnackbar('Procedure cancelled successfully', {
          variant: 'success',
        })
        refetch?.()
        onClose()
      } else {
        enqueueSnackbar('Error cancelling procedure', { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar('Error cancelling procedure', { variant: 'error' })
    }
  }

  const handleOnSubmit = () => {
    handleSubmit(onSubmit)()
  }

  return {
    control,
    errors,
    isLoadingCancel,
    handleOnSubmit,
    cancellationOptions,
    isOtherSelected,
  }
}
